// 📄 cambioService.ts
// Solicitudes de cambio del proyecto (plan-proyectos-generales.md, G5).
// Cualquier miembro (también el cliente, que suele ser quien pide el cambio)
// crea una solicitud; el equipo estima el impacto y la ENVÍA A APROBACIÓN
// eligiendo quiénes aprueban ESE cambio ("basta con uno" o "todos"). Nadie
// aprueba su propia solicitud. Aprobada, el equipo la implementa (puede
// crear tareas vinculadas a un hito) y la marca como implementada.
//
// firestore.rules (projects/{id}/cambios) hace cumplir lo importante, no
// solo la UI: el equipo no puede poner "Aprobada"/"Rechazada" a mano, solo
// un aprobador asignado con su propia decisión, y el solicitante nunca puede
// ser aprobador. Por eso las decisiones se guardan también en listas simples
// (`decididoPor`, `aprobadoPor`) que las reglas pueden comparar.
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  getDocs,
  arrayUnion,
  Timestamp,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { ProjectMember } from './projectService.ts';
import { queueEmail } from './mailService.ts';

export type CambioEstado = 'Nueva' | 'En evaluación' | 'En aprobación' | 'Aprobada' | 'Rechazada' | 'En implementación' | 'Implementada' | 'Cancelada';
export const ESTADOS_CAMBIO: CambioEstado[] = ['Nueva', 'En evaluación', 'En aprobación', 'Aprobada', 'Rechazada', 'En implementación', 'Implementada', 'Cancelada'];
export type CambioPrioridad = 'Baja' | 'Media' | 'Alta' | 'Crítica';
export const PRIORIDADES_CAMBIO: CambioPrioridad[] = ['Baja', 'Media', 'Alta', 'Crítica'];
export type ModoAprobacionCambio = 'uno' | 'todos';

export interface CambioFormData {
  titulo: string;
  descripcion: string;
  motivo: string;
  prioridad: CambioPrioridad;
  area: string; // área del proyecto o solución SAP ("" = sin área)
  hitoId: string; // hito afectado ("" = ninguno)
}

export interface ImpactoCambio {
  horas: number | null;
  costo: number | null;
  dias: number | null; // días de retraso en el cronograma
  notas: string;
}

export interface DecisionCambio {
  uid: string;
  nombre: string;
  decision: 'aprobar' | 'rechazar';
  comentario: string;
  en: any;
}

export interface EventoCambio {
  tipo: string;
  actorUid: string;
  actorNombre: string;
  detalle: string;
  en: any;
}

export interface Cambio extends CambioFormData {
  id: string;
  estado: CambioEstado;
  hitoNombre: string;
  impacto: ImpactoCambio;
  aprobadores: string[];
  aprobadoresNombres: Record<string, string>;
  modo: ModoAprobacionCambio;
  decisiones: DecisionCambio[];
  decididoPor: string[];
  aprobadoPor: string[];
  solicitadoPor: string;
  solicitadoPorNombre: string;
  creadoEn: any;
  historial: EventoCambio[];
}

export const IMPACTO_VACIO: ImpactoCambio = { horas: null, costo: null, dias: null, notas: '' };

export const tieneImpacto = (i: ImpactoCambio) => i.horas != null || i.costo != null || i.dias != null;

/** "5 h · costo 150.000 · 1 día de retraso" (lo que falta se muestra como "–"). */
export const formatoImpacto = (i: ImpactoCambio): string => {
  const horas = i.horas != null ? `${i.horas} h` : '– h';
  const costo = `costo ${i.costo != null ? i.costo.toLocaleString('es-CO') : '–'}`;
  const dias = i.dias != null ? `${i.dias} ${i.dias === 1 ? 'día' : 'días'} de retraso` : '– días de retraso';
  return `${horas} · ${costo} · ${dias}`;
};

const cambiosRef = (projectId: string) => collection(db, "projects", projectId, "cambios");
const cambioRef = (projectId: string, id: string) => doc(db, "projects", projectId, "cambios", id);

const evento = (tipo: string, actorUid: string, actorNombre: string, detalle = ''): EventoCambio =>
  ({ tipo, actorUid, actorNombre, detalle, en: Timestamp.now() });

const escapeHtml = (str: string): string =>
  str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);

const avisar = (to: string[], asunto: string, cuerpo: string) =>
  queueEmail({ to, subject: asunto, html: cuerpo }).catch((err) => console.error('Error enviando el correo de la solicitud de cambio:', err));

export const getCambios = async (projectId: string): Promise<Cambio[]> => {
  const snap = await getDocs(cambiosRef(projectId));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Cambio, "id">) }))
    .sort((a, b) => (b.creadoEn?.toMillis?.() || 0) - (a.creadoEn?.toMillis?.() || 0));
};

export const createCambio = async (projectId: string, data: CambioFormData, hitoNombre: string, uid: string, nombre: string): Promise<string> => {
  if (!data.titulo.trim()) throw new Error("El título es obligatorio.");
  if (!data.descripcion.trim()) throw new Error("Describe qué se quiere cambiar.");
  const ref = doc(cambiosRef(projectId));
  await setDoc(ref, {
    titulo: data.titulo.trim(),
    descripcion: data.descripcion.trim(),
    motivo: data.motivo.trim(),
    prioridad: data.prioridad,
    area: data.area,
    hitoId: data.hitoId,
    hitoNombre: data.hitoId ? hitoNombre : '',
    estado: 'Nueva' as CambioEstado,
    impacto: IMPACTO_VACIO,
    aprobadores: [],
    aprobadoresNombres: {},
    modo: 'uno' as ModoAprobacionCambio,
    decisiones: [],
    decididoPor: [],
    aprobadoPor: [],
    solicitadoPor: uid,
    solicitadoPorNombre: nombre,
    creadoEn: serverTimestamp(),
    historial: [evento('creada', uid, nombre)]
  });
  return ref.id;
};

/** El solicitante (o el equipo) corrige los datos mientras la solicitud es Nueva o En evaluación. */
export const updateCambioDatos = async (projectId: string, id: string, data: CambioFormData, hitoNombre: string, uid: string, nombre: string) => {
  if (!data.titulo.trim()) throw new Error("El título es obligatorio.");
  await updateDoc(cambioRef(projectId, id), {
    titulo: data.titulo.trim(),
    descripcion: data.descripcion.trim(),
    motivo: data.motivo.trim(),
    prioridad: data.prioridad,
    area: data.area,
    hitoId: data.hitoId,
    hitoNombre: data.hitoId ? hitoNombre : '',
    historial: arrayUnion(evento('editada', uid, nombre))
  });
};

/**
 * Equipo: estimación de impacto (horas, costo, días de retraso). Guardarla
 * por primera vez pasa la solicitud de Nueva a En evaluación: alguien del
 * equipo la está atendiendo (decisión del usuario, sin botón aparte).
 */
export const updateImpacto = async (projectId: string, cambio: Cambio, impacto: ImpactoCambio, uid: string, nombre: string) => {
  const pasaAEvaluacion = cambio.estado === 'Nueva';
  await updateDoc(cambioRef(projectId, cambio.id), {
    impacto,
    ...(pasaAEvaluacion ? { estado: 'En evaluación' as CambioEstado } : {}),
    historial: arrayUnion(evento(pasaAEvaluacion ? 'estimó el impacto (En evaluación)' : 'actualizó el impacto', uid, nombre, formatoImpacto(impacto)))
  });
};

/** Equipo: transiciones que no son decisiones (evaluar, implementar, implementada, cancelar). */
export const cambiarEstado = async (projectId: string, id: string, estado: CambioEstado, uid: string, nombre: string, detalle = '') => {
  if (estado === 'Aprobada' || estado === 'Rechazada' || estado === 'En aprobación') throw new Error("Ese estado solo cambia con el proceso de aprobación.");
  await updateDoc(cambioRef(projectId, id), { estado, historial: arrayUnion(evento(estado, uid, nombre, detalle)) });
};

/**
 * Equipo: envía (o reenvía) la solicitud a aprobación con sus aprobadores.
 * Reinicia las decisiones previas (si se reasigna, se vota de nuevo).
 */
export const enviarAAprobacion = async (
  projectId: string, cambio: Cambio, aprobadores: string[], modo: ModoAprobacionCambio, miembros: ProjectMember[],
  uid: string, nombre: string, proyectoNombre: string, urlProyecto: string
) => {
  const lista = Array.from(new Set(aprobadores)).filter((a) => a !== cambio.solicitadoPor);
  if (!lista.length) throw new Error("Elige al menos un aprobador (quien pidió el cambio no puede aprobarlo).");
  const nombres: Record<string, string> = {};
  for (const a of lista) {
    const m = miembros.find((x) => x.userId === a);
    if (!m) throw new Error("Un aprobador ya no es miembro del proyecto.");
    nombres[a] = m.email;
  }
  await updateDoc(cambioRef(projectId, cambio.id), {
    estado: 'En aprobación' as CambioEstado,
    aprobadores: lista,
    aprobadoresNombres: nombres,
    modo: lista.length > 1 ? modo : 'uno',
    decisiones: [],
    decididoPor: [],
    aprobadoPor: [],
    historial: arrayUnion(evento('enviada a aprobación', uid, nombre, `${Object.values(nombres).join(', ')} · ${modo === 'todos' && lista.length > 1 ? 'aprueban todos' : 'basta con uno'}`))
  });
  avisar(Object.values(nombres), `[Cambio] Aprobación pendiente: ${cambio.titulo}`, `
    <p>Se te asignó aprobar una solicitud de cambio en el proyecto <strong>${escapeHtml(proyectoNombre)}</strong>.</p>
    <p><strong>${escapeHtml(cambio.titulo)}</strong> (prioridad ${cambio.prioridad})</p>
    <p style="white-space:pre-wrap">${escapeHtml(cambio.descripcion)}</p>
    ${tieneImpacto(cambio.impacto) ? `<p>Impacto estimado: ${escapeHtml(formatoImpacto(cambio.impacto))}</p>` : ''}
    <p><a href="${urlProyecto}/cambios">Abrir Solicitudes de cambio</a></p>
  `);
};

/**
 * Aprobador asignado: registra su decisión. "Rechazar" cierra la solicitud;
 * "Aprobar" la aprueba si basta con uno, o cuando aprobaron todos.
 */
export const decidirCambio = async (
  projectId: string, cambio: Cambio, decision: 'aprobar' | 'rechazar', comentario: string,
  uid: string, nombre: string, emailSolicitante: string, proyectoNombre: string, urlProyecto: string
): Promise<CambioEstado> => {
  if (uid === cambio.solicitadoPor) throw new Error("No puedes aprobar tu propia solicitud.");
  if (!cambio.aprobadores.includes(uid)) throw new Error("No eres aprobador de esta solicitud.");
  if (cambio.decididoPor.includes(uid)) throw new Error("Ya registraste tu decisión.");
  const aprobadoPor = decision === 'aprobar' ? [...cambio.aprobadoPor, uid] : cambio.aprobadoPor;
  const todosAprobaron = cambio.aprobadores.every((a) => aprobadoPor.includes(a));
  const estado: CambioEstado = decision === 'rechazar' ? 'Rechazada' : (cambio.modo === 'uno' || todosAprobaron ? 'Aprobada' : 'En aprobación');
  await updateDoc(cambioRef(projectId, cambio.id), {
    estado,
    decididoPor: [...cambio.decididoPor, uid],
    aprobadoPor,
    decisiones: [...cambio.decisiones, { uid, nombre, decision, comentario: comentario.trim(), en: Timestamp.now() }],
    historial: arrayUnion(evento(decision === 'aprobar' ? 'aprobó' : 'rechazó', uid, nombre, comentario.trim()))
  });
  if (estado !== 'En aprobación' && emailSolicitante) {
    avisar([emailSolicitante], `[Cambio] ${estado}: ${cambio.titulo}`, `
      <p>Tu solicitud de cambio en <strong>${escapeHtml(proyectoNombre)}</strong> fue <strong>${estado.toLowerCase()}</strong>.</p>
      <p><strong>${escapeHtml(cambio.titulo)}</strong></p>
      ${comentario.trim() ? `<p>Comentario de ${escapeHtml(nombre)}: ${escapeHtml(comentario.trim())}</p>` : ''}
      <p><a href="${urlProyecto}/cambios">Abrir Solicitudes de cambio</a></p>
    `);
  }
  return estado;
};

/** Aprobadores sugeridos al enviar: el creador del proyecto y los usuarios cliente activos (nunca el solicitante). */
export const aprobadoresSugeridos = (creadoPor: string, miembros: ProjectMember[], solicitadoPor: string): string[] =>
  Array.from(new Set([
    creadoPor,
    ...miembros.filter((m) => m.rol === 'cliente' && m.activo !== false).map((m) => m.userId)
  ])).filter((u) => u && u !== solicitadoPor && miembros.some((m) => m.userId === u && m.activo !== false));
